import { safe } from "pb.safe";
import type { Identity } from "pb.types";
import { v4 as uuidv4 } from "uuid";
import { z } from "zod";

import { logger } from "../shared/logger";

const environments = {
	development: "wss://global.posconnector.myzeller.dev",
	staging: "wss://global.posconnector.myzeller.show",
	production: "wss://global.posconnector.myzeller.com",
} as const;

/**
 * https://developer.mozilla.org/en-US/docs/Web/API/CloseEvent/code
 */
export const WS_CLOSE_EVENT_CODE_1000_NORMAL_CLOSURE = 1000;

export type POSConnectorClient = Identity<
	{
		ready: boolean;
		close: (code?: number) => void;
		ping: () => Error | void;
		send: (payload: Payload) => Error | void;
	} & {
		[Key in keyof POSConnectorClient.Handlers]-?: (
			callback: POSConnectorClient.Handlers[Key],
		) => void;
	}
>;

export namespace POSConnectorClient {
	export type Options = Identity<
		{
			verbose?: boolean;
			authToken: string;
			deviceUuid: string;
			environment?: keyof typeof environments;
		} & Handlers
	>;

	export type Handlers = {
		onOpen?: () => void;
		onClose?: (error: CloseEvent) => void;
		onError?: (error: Event) => void;
		onMessage?: (message: Message | Error) => void;
	};
}

export function POSConnectorClient({
	verbose,
	authToken,
	deviceUuid,
	environment,

	// deprecated, use methods on client object instead of constructor
	onOpen: onOpenInitial,
	onClose: onCloseInitial,
	onError: onErrorInitial,
	onMessage: onMessageInitial,
}: POSConnectorClient.Options): POSConnectorClient {
	const url = formatUrl({ environment });
	const Protocol = "posconnector";
	const Authorization = authToken;
	const DeviceUuid = deviceUuid;

	const protocols = [
		Protocol,
		`Authorization|${Authorization}`,
		`DeviceUuid|${DeviceUuid}`,
	];
	const socket = new WebSocket(url, protocols);

	if (verbose) {
		socket.addEventListener(...socketDebug("open"));
		socket.addEventListener(...socketDebug("close"));
		socket.addEventListener(...socketDebug("error"));
		socket.addEventListener(...socketDebug("message"));
	}

	const handlers: POSConnectorClient.Handlers = {
		onOpen: onOpenInitial,
		onClose: onCloseInitial,
		onError: onErrorInitial,
		onMessage: onMessageInitial,
	};

	socket.addEventListener("open", () => {
		handlers.onOpen?.();
	});
	socket.addEventListener("close", (event) => {
		handlers.onClose?.(event);
	});
	socket.addEventListener("error", (event) => {
		handlers.onError?.(event);
	});
	socket.addEventListener("message", (event) => {
		if (handlers.onMessage) {
			const message = safe(() => MessageSchema.parse(JSON.parse(event.data)));
			handlers.onMessage(message);
		}
	});

	const isOpen = () => {
		return socket.readyState === socket.OPEN;
	};

	return {
		get ready() {
			return isOpen();
		},
		close: (code?: number) => {
			handlers.onOpen = undefined;
			handlers.onClose = undefined;
			handlers.onError = undefined;
			handlers.onMessage = undefined;
			if (!isOpen()) {
				return new Error("Socket not ready.");
			}
			socket.close(code ?? WS_CLOSE_EVENT_CODE_1000_NORMAL_CLOSURE);
			return;
		},
		ping: () => {
			if (!isOpen()) {
				return new Error("Socket not ready.");
			}
			socket.send(
				JSON.stringify({
					action: "ping",
					eventId: uuidv4(),
					timestamp: Date.now().toString(),
				}),
			);
			return;
		},
		send: (payload: Payload) => {
			if (!isOpen()) {
				return new Error("Socket not ready.");
			}
			socket.send(JSON.stringify(payload));
			return;
		},
		onOpen: (callback) => {
			handlers.onOpen = callback;
		},
		onClose: (callback) => {
			handlers.onClose = callback;
		},
		onError: (callback) => {
			handlers.onError = callback;
		},
		onMessage: (callback) => {
			handlers.onMessage = callback;
		},
	};
}

// ============================================================================

namespace formatUrl {
	export type Props = {
		environment?: keyof typeof environments;
	};
}

function formatUrl({ environment }: formatUrl.Props) {
	if (environment) {
		return environments[environment];
	} else if (ipRegex.test(window.location.hostname)) {
		return environments["development"];
	}
	/* v8 ignore next 6 -- hostname-derived URL not exercisable in browser test env */
	if (window.location.hostname.endsWith(".dev")) {
		return environments["development"];
	} else if (window.location.hostname.endsWith(".show")) {
		return environments["staging"];
	}
	return environments["production"];
}

const ipRegex = /^(localhost|(\d{1,3})(\.\d{1,3}){3})(:\d{1,5})?$/;

// ============================================================================

const socketDebug = <TType extends string>(
	type: TType,
): [type: TType, callback: (...args: unknown[]) => void] => [
	type,
	(...args) => logger.debug(POSConnectorClient.name, { type, ...args }),
];

// ============================================================================

export const MessageSchema = z.object({
	action: z.string(),
	eventId: z.string(),
	/**
	 * Correlates every frame in a single transaction flow. Present on request,
	 * acknowledge and response frames; absent on transaction events.
	 */
	requestId: z.string().optional(),
	timestamp: z.string(),
	data: z.union([z.null(), z.record(z.unknown())]),
	// `getPairedDevices` replies omit `error` entirely.
	error: z
		.object({ type: z.string(), message: z.string() })
		.nullable()
		.optional(),
});

export type Message = z.infer<typeof MessageSchema>;
export type { Message as POSConnectorMessage };

const PayloadPurchaseData = z.object({
	/**
	 * ISO Timestamp.
	 */
	timestamp: z.string(),
	/**
	 * Forwarded UUID.
	 */
	sessionUuid: z.string(),
	/**
	 * Merchant-defined reference in any format.
	 */
	externalReference: z.string(),
	/**
	 * Generated ID to use for transaction as UUID.
	 */
	transactionUuid: z.string(),
	/**
	 * Amount in subunit (e.g. cents) of currency.
	 */
	amount: z.number(),
	/**
	 * Amount in subunit (e.g. cents) of currency.
	 */
	tipAmount: z.number(),

	terminalContact: z.boolean().optional(),
	terminalNote: z.boolean().optional(),
	terminalReceiptPrint: z.boolean().optional(),
	terminalReceipt: z.boolean().optional(),
});

const PayloadRefundMatchedData = z.object({
	/**
	 * Amount in subunit (e.g. cents) of currency.
	 */
	amount: z.number(),
	/**
	 * Merchant-defined reference in any format.
	 */
	externalReference: z.string(),
	originalIsoProcessingCode: z.string(),
	originalTransactionUuid: z.string(),
	sessionUuid: z.string(),
	transactionUuid: z.string(),
});

const PayloadRefundUnmatchedData = z.object({
	/**
	 * Amount in subunit (e.g. cents) of currency.
	 */
	amount: z.number(),
	/**
	 * Merchant-defined reference in any format.
	 */
	externalReference: z.string(),
	isCardPresent: z.boolean(),
	sessionUuid: z.string(),
});

const PayloadSignatureVerificationData = z.object({
	event: z.literal("SIGNATURE_VERIFICATION"),
	isValid: z.boolean(),
	sessionUuid: z.string(),
});

const PayloadTransactionData = z.object({
	event: z.union([
		z.literal("CANCEL"),
		z.literal("PROCEED"),
		z.literal("SKIP"),
	]),
	sessionUuid: z.string(),
});

const PayloadAcknowledgedData = z.object({
	state: z.literal("ACKNOWLEDGED"),
	sessionUuid: z.string(),
});

/**
 * Locally generated UUID.
 */
const eventId = z.string();
/**
 * Unix timestamp in milliseconds as a string.
 */
const timestamp = z.string();
/**
 * Correlates every frame in a single transaction flow. Distinct from
 * `sessionUuid` and reused across the request, acknowledge and response frames
 * of one flow.
 */
const requestId = z.string();

const RequestPayloadSchema = z.object({
	action: z.union([z.literal("purchase"), z.literal("refund")]),
	eventId,
	requestId,
	timestamp,
	data: z.union([
		PayloadPurchaseData,
		PayloadRefundMatchedData,
		PayloadRefundUnmatchedData,
	]),
});

const AcknowledgePayloadSchema = z.object({
	action: z.union([z.literal("purchase"), z.literal("refund")]),
	eventId,
	requestId,
	timestamp,
	data: PayloadAcknowledgedData,
});

const TransactionEventPayloadSchema = z.object({
	action: z.literal("transactionEvent"),
	eventId,
	timestamp,
	data: PayloadTransactionData,
});

const SignatureVerificationPayloadSchema = z.object({
	action: z.literal("signatureVerification"),
	eventId,
	/**
	 * Echoed back from the terminal-initiated signature event. Optional during
	 * staged rollout; absent when the incoming event omitted it.
	 */
	requestId: requestId.optional(),
	timestamp,
	data: PayloadSignatureVerificationData,
});

const GetPairedDevicesPayloadSchema = z.object({
	action: z.literal("getPairedDevices"),
	eventId,
	timestamp,
	data: z.null(),
});

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const PayloadSchema = z.union([
	RequestPayloadSchema,
	AcknowledgePayloadSchema,
	TransactionEventPayloadSchema,
	SignatureVerificationPayloadSchema,
	GetPairedDevicesPayloadSchema,
]);

export type Payload = z.infer<typeof PayloadSchema>;
export type { Payload as POSConnectorPayload };
